import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Search, LogOut, User, Menu, X, Shield, BarChart3, Sparkles, MessageCircle, BellOff, CalendarDays, Library as LibraryIcon } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import PremiumBadge from './PremiumBadge';
import AdminBadge from './AdminBadge';
import ModeratorBadge from './ModeratorBadge';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useEventsEnabled } from '../hooks/useEventsEnabled';
import { useLibraryEnabled } from '../hooks/useLibraryEnabled';

const Header = ({ onSearchOpen, onSearchClear, onNotificationsOpen, onNavigate, searchEnabled = true }) => {
  const { profile, signOut } = useAuth();
  const eventsEnabled = useEventsEnabled();
  const showCalendar = eventsEnabled === true || profile?.is_admin === true;
  const libraryEnabled = useLibraryEnabled();
  const showLibrary = libraryEnabled === true || profile?.is_admin === true;
  const { permission: pushPermission, isSubscribed, requestAndSubscribe, unsubscribe: unsubscribePush } = usePushNotifications();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const notifTimerRef = useRef(null);
  const userMenuTimerRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const res = await api.getNotifications(profile.id);
      setNotifications(res.data || []);
      setUnreadCount((res.data || []).filter(n => !n.is_read).length);
    } catch {
      // Notifications unavailable — fail silently
    }
  }, [profile?.id]);

  const fetchUnreadMessages = useCallback(async () => {
    if (!profile?.id || !profile?.is_premium) { setUnreadMessages(0); return; }
    try {
      const res = await api.unreadMessagesCount(profile.id);
      setUnreadMessages(res.data?.unread || 0);
    } catch {
      setUnreadMessages(0);
    }
  }, [profile?.id, profile?.is_premium]);

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications();
      fetchUnreadMessages();
      const interval = setInterval(() => {
        fetchUnreadMessages();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [profile?.id, fetchNotifications, fetchUnreadMessages]);

  useEffect(() => {
    return () => {
      clearTimeout(notifTimerRef.current);
      clearTimeout(userMenuTimerRef.current);
    };
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead(profile.id);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearchOpen?.(searchQuery);
      setIsSearchActive(true);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearchActive(false);
    onSearchClear?.();
  };

  return (
    <header className="sticky top-0 z-50 glass-card border-b border-white/20 safe-area-pt">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <h1 
              className="font-heading text-2xl font-semibold text-primary cursor-pointer" 
              data-testid="app-logo"
              onClick={() => { handleClearSearch(); onNavigate?.('feed'); }}
            >
              Sirati
            </h1>
          </div>

          {/* Desktop Search — only where search results are actually shown (the feed) */}
          {searchEnabled && (
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted dark:text-primary" />
                <input
                  type="text"
                  placeholder="Search posts or users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-10 py-2.5 bg-white/70 dark:bg-white dark:text-gray-800 dark:placeholder:text-primary rounded-full border border-primary/20 focus:ring-2 focus:ring-primary transition-all text-sm text-text-main"
                  data-testid="search-input"
                />
                {(searchQuery || isSearchActive) && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-primary/10 rounded-full transition-colors"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-4 h-4 text-text-muted" aria-hidden="true" />
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Mobile Search Button */}
            {searchEnabled && (
              <button
                aria-label="Search"
                className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10"
                onClick={() => setShowSearch(!showSearch)}
                data-testid="mobile-search-btn"
              >
                <Search className="w-5 h-5 text-primary" aria-hidden="true" />
              </button>
            )}

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notifications */}
            <div className="relative">
              <span className="sr-only" aria-live="polite" aria-atomic="true">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : ''}
              </span>
              <button
                onClick={() => {
                  const next = !showNotifications;
                  setShowNotifications(next);
                  clearTimeout(notifTimerRef.current);
                  if (next) {
                    notifTimerRef.current = setTimeout(() => setShowNotifications(false), 5000);
                  }
                }}
                aria-label={unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'}
                aria-expanded={showNotifications}
                className="relative min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5 text-primary" aria-hidden="true" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center" aria-hidden="true">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] glass-card rounded-arch-lg shadow-xl overflow-hidden z-50"
                    data-testid="notifications-dropdown"
                    onMouseEnter={() => clearTimeout(notifTimerRef.current)}
                    onMouseLeave={() => { notifTimerRef.current = setTimeout(() => setShowNotifications(false), 5000); }}
                  >
                    <div className="p-4 border-b border-white/20 flex items-center justify-between">
                      <h3 className="font-heading font-medium text-primary">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-sm text-primary hover:underline"
                          data-testid="mark-all-read-btn"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-text-muted text-sm">No notifications yet</p>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            onClick={async () => {
                              if (!notif.is_read) {
                                try { await api.markNotificationRead(notif.id); fetchNotifications(); } catch {}
                              }
                            }}
                            className={`p-4 border-b border-border last:border-0 hover:bg-background/50 cursor-pointer ${!notif.is_read ? 'bg-accent-teal/5' : ''}`}
                            data-testid={`notification-${notif.id}`}
                          >
                            <p className="text-sm text-text-main">{notif.message}</p>
                            <p className="text-xs text-text-muted mt-1">
                              {(() => {
                                const diff = Date.now() - new Date(notif.created_at).getTime();
                                const mins = Math.floor(diff / 60000);
                                if (mins < 60) return `${mins}m ago`;
                                const hrs = Math.floor(mins / 60);
                                if (hrs < 24) return `${hrs}h ago`;
                                return `${Math.floor(hrs / 24)}d ago`;
                              })()}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                    {pushPermission !== 'unsupported' && pushPermission !== 'denied' && (
                      <div className="p-3 border-t border-white/20">
                        {isSubscribed ? (
                          <button
                            onClick={unsubscribePush}
                            className="w-full flex items-center gap-2 text-xs text-text-muted hover:text-text-main justify-center py-1"
                            data-testid="push-unsubscribe-btn"
                          >
                            <BellOff className="w-3.5 h-3.5" aria-hidden="true" />
                            Turn off push notifications
                          </button>
                        ) : (
                          <button
                            onClick={requestAndSubscribe}
                            className="w-full flex items-center gap-2 text-xs text-primary hover:underline justify-center py-1 font-medium"
                            data-testid="push-subscribe-btn"
                          >
                            <Bell className="w-3.5 h-3.5" aria-hidden="true" />
                            Enable push notifications
                          </button>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Menu — desktop only; mobile users access these items via the hamburger */}
            <div className="relative hidden lg:block">
              <button
                onClick={() => {
                  const next = !showUserMenu;
                  setShowUserMenu(next);
                  clearTimeout(userMenuTimerRef.current);
                  if (next) {
                    userMenuTimerRef.current = setTimeout(() => setShowUserMenu(false), 5000);
                  }
                }}
                aria-label={`${profile?.display_name || 'Account'} menu`}
                aria-expanded={showUserMenu}
                className="relative flex items-center gap-2 min-h-[44px] min-w-[44px] justify-center rounded-full hover:bg-card transition-colors"
                data-testid="user-menu-btn"
              >
                <img
                  src={profile?.avatar_url || defaultAvatar(profile?.gender)}
                  alt={profile?.display_name}
                  className="w-8 h-8 rounded-full object-cover border-2 border-border"
                />
                {unreadMessages > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-primary rounded-full border-2 border-background" data-testid="messages-unread-dot" />
                )}
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-card rounded-arch-lg shadow-xl border border-border overflow-hidden z-50"
                    data-testid="user-menu-dropdown"
                    onMouseEnter={() => clearTimeout(userMenuTimerRef.current)}
                    onMouseLeave={() => { userMenuTimerRef.current = setTimeout(() => setShowUserMenu(false), 5000); }}
                  >
                    <div className="p-4 border-b border-border">
                      <p className="font-medium text-text-main flex items-center gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserMenu(false);
                            onNavigate?.(`user:${profile?.display_name}`);
                          }}
                          className="hover:opacity-70 transition-opacity"
                          data-testid="user-menu-name-link"
                        >
                          {profile?.display_name}
                        </button>
                        <AdminBadge show={profile?.is_admin} />
                        <ModeratorBadge show={!profile?.is_admin && profile?.is_moderator} />
                        <PremiumBadge show={profile?.is_premium && !profile?.is_admin} size={14} />
                      </p>
                    </div>
                    <div className="p-2">
                      {!profile?.is_premium && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            onNavigate?.('premium');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-primary font-medium"
                          data-testid="upgrade-premium-link"
                        >
                          <Sparkles className="w-4 h-4" />
                          Upgrade to Sirati+
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onNavigate?.('profile');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-text-main"
                        data-testid="profile-link"
                      >
                        <User className="w-4 h-4" />
                        Edit Profile
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          if (profile?.is_premium) {
                            onNavigate?.('messages');
                          } else {
                            onNavigate?.('premium');
                          }
                        }}
                        className="w-full flex items-center justify-between gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-text-main"
                        data-testid="messages-link"
                      >
                        <span className="flex items-center gap-3">
                          <MessageCircle className="w-4 h-4" />
                          Messages
                          {!profile?.is_premium && (
                            <Sparkles className="w-3 h-3 text-yellow-500" title="Sirati+ feature" />
                          )}
                        </span>
                        {unreadMessages > 0 && (
                          <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="messages-unread-badge">
                            {unreadMessages > 9 ? '9+' : unreadMessages}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onNavigate?.('dashboard');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-text-main"
                        data-testid="dashboard-link"
                      >
                        <BarChart3 className="w-4 h-4" />
                        My Dashboard
                      </button>
                      {showCalendar && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            onNavigate?.('calendar');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-text-main"
                          data-testid="calendar-link"
                        >
                          <CalendarDays className="w-4 h-4" aria-hidden="true" />
                          Calendar
                        </button>
                      )}
                      {showLibrary && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            onNavigate?.('library');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-text-main"
                          data-testid="library-link"
                        >
                          <LibraryIcon className="w-4 h-4" aria-hidden="true" />
                          Library
                        </button>
                      )}
                      {/* Admin Dashboard - only show for admins */}
                      {profile?.is_admin && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            onNavigate?.('admin');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-primary/10 text-left text-primary"
                          data-testid="admin-dashboard-link"
                        >
                          <Shield className="w-4 h-4" />
                          Admin Dashboard
                        </button>
                      )}
                      <button
                        onClick={signOut}
                        className="w-full flex items-center gap-3 px-4 py-2 rounded-arch hover:bg-red-50 dark:hover:bg-red-900/20 text-left text-red-500"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Menu Button */}
            <button
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-card"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <AnimatePresence>
          {showSearch && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onSubmit={handleSearch}
              className="md:hidden mt-3"
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search posts or users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 bg-white/70 dark:input-white-dark rounded-full border border-primary/20 focus:ring-2 focus:ring-primary transition-all text-sm text-text-main"
                  data-testid="mobile-search-input"
                />
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Mobile Hamburger Menu Panel */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
              data-testid="mobile-menu-panel"
            >
              <div className="pt-3 pb-1 border-t border-white/20 mt-3">
                {/* Profile info row */}
                <div className="flex items-center gap-3 px-2 py-3 mb-1">
                  <img
                    src={profile?.avatar_url || defaultAvatar(profile?.gender)}
                    alt={profile?.display_name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-border flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-text-main truncate flex items-center gap-1 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          onNavigate?.(`user:${profile?.display_name}`);
                        }}
                        className="hover:opacity-70 transition-opacity truncate"
                        data-testid="mobile-menu-name-link"
                      >
                        {profile?.display_name}
                      </button>
                      <AdminBadge show={profile?.is_admin} />
                      <ModeratorBadge show={!profile?.is_admin && profile?.is_moderator} />
                      <PremiumBadge show={profile?.is_premium && !profile?.is_admin} size={14} />
                    </p>
                  </div>
                </div>

                {/* Nav items */}
                {!profile?.is_premium && (
                  <button
                    onClick={() => { setMobileMenuOpen(false); onNavigate?.('premium'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-primary font-medium"
                    data-testid="mobile-upgrade-premium-link"
                  >
                    <Sparkles className="w-4 h-4 flex-shrink-0" />
                    Upgrade to Sirati+
                  </button>
                )}
                <button
                  onClick={() => { setMobileMenuOpen(false); onNavigate?.('profile'); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-text-main"
                  data-testid="mobile-profile-link"
                >
                  <User className="w-4 h-4 flex-shrink-0" />
                  Edit Profile
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onNavigate?.(profile?.is_premium ? 'messages' : 'premium');
                  }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-text-main"
                  data-testid="mobile-messages-link"
                >
                  <span className="flex items-center gap-3">
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                    Messages
                    {!profile?.is_premium && (
                      <Sparkles className="w-3 h-3 text-yellow-500" />
                    )}
                  </span>
                  {unreadMessages > 0 && (
                    <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); onNavigate?.('dashboard'); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-text-main"
                  data-testid="mobile-dashboard-link"
                >
                  <BarChart3 className="w-4 h-4 flex-shrink-0" />
                  My Dashboard
                </button>
                {showCalendar && (
                  <button
                    onClick={() => { setMobileMenuOpen(false); onNavigate?.('calendar'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-text-main"
                    data-testid="mobile-calendar-link"
                  >
                    <CalendarDays className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    Calendar
                  </button>
                )}
                {showLibrary && (
                  <button
                    onClick={() => { setMobileMenuOpen(false); onNavigate?.('library'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-text-main"
                    data-testid="mobile-library-menu-link"
                  >
                    <LibraryIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    Library
                  </button>
                )}
                {profile?.is_admin && (
                  <button
                    onClick={() => { setMobileMenuOpen(false); onNavigate?.('admin'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-primary/10 text-left text-primary"
                    data-testid="mobile-admin-dashboard-link"
                  >
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    Admin Dashboard
                  </button>
                )}
                <button
                  onClick={() => { setMobileMenuOpen(false); signOut(); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-arch hover:bg-red-50 dark:hover:bg-red-900/20 text-left text-red-500"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-4 h-4 flex-shrink-0" />
                  Sign Out
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
